/* eslint-disable react/no-array-index-key */
import { FC, useState } from 'react';
import CheckboxDropdown from '../../../Components/Form/Inputs/CheckboxDropdown';
import { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from './BasisCalculator.module.scss';
import { BasisAdjustment, BasisPeriod, basisPeriods } from './BasisCalculatorDefinitions';
import { IBasisCalculatorCalculationResult } from './BasisCalculatorService';

interface IBasisCalculatorGridProps {
    calculationResult?: IBasisCalculatorCalculationResult;
}

const BasisCalculatorGrid: FC<IBasisCalculatorGridProps> = (props: IBasisCalculatorGridProps) => {
    const [translations] = useLanguage();
    const [selectedPeriods, setSelectedPeriods] = useState<BasisPeriod[]>([BasisPeriod.Basis]);

    const basisOptions: SelectInputOption<BasisPeriod>[] = basisPeriods.map((period) => ({
        label: translations.calculators.basis.basisPeriod[period],
        value: period,
    }));

    const deciles = [
        {
            label: translations.calculators.basis.basisAdjustment[BasisAdjustment.Minimum],
            value: 0,
        },
        {
            label: '10%',
            value: 1,
        },
        {
            label: '20%',
            value: 2,
        },
        {
            label: '30%',
            value: 3,
        },
        {
            label: '40%',
            value: 4,
        },
        {
            label: '50%',
            value: 5,
        },
        {
            label: '60%',
            value: 6,
        },
        {
            label: '70%',
            value: 7,
        },
        {
            label: '80%',
            value: 8,
        },
        {
            label: '90%',
            value: 9,
        },
        {
            label: translations.calculators.basis.basisAdjustment[BasisAdjustment.Maximum],
            value: 10,
        },
    ];

    const randomKey = (min: number = 1, max: number = 1000000000): number => Math.floor(Math.random() * (max - min + 1)) + min;

    const handleIntervalChange = (values: BasisPeriod[]) => {
        const orderMap: Map<BasisPeriod, number> = new Map();
        basisOptions.forEach((option, index) => {
            orderMap.set(option.value, index);
        });
        const sortedValues = values.sort((a, b) => {
            const indexA = orderMap.get(a) ?? -1;
            const indexB = orderMap.get(b) ?? -1;
            return indexA - indexB;
        });
        setSelectedPeriods(sortedValues);
    };

    return (
        <div className={styles.basis_calculator_grid}>
            <div className={styles.basis_calculator_grid_interval}>
                <CheckboxDropdown
                    values={selectedPeriods}
                    options={basisOptions}
                    handleOptionSelect={handleIntervalChange}
                    placeholder={translations.calculators.basis.form.selectIntervals}
                    track={{ eventCategory: 'BasisCalculator', eventAction: 'SelectedIntervalsChanged', eventValue: selectedPeriods }}
                />
            </div>
            <div className={styles.basis_calculator_grid_table}>
                <div key={randomKey()} className={styles.basis_calculator_grid_table_row}>
                    <div className={styles.basis_calculator_grid_table_cell_empty}> </div>
                    <div className={styles.basis_calculator_grid_table_cell_average}>
                        <strong>{translations.text.averageShort}</strong>
                    </div>
                    {deciles.map((decile, index) => (
                        <div className={styles[`basis_calculator_grid_table_cell_decile${index}`]}>
                            <strong>{decile.label}</strong>
                        </div>
                    ))}
                </div>
                {selectedPeriods.map((period) => (
                    <div key={randomKey()} className={styles.basis_calculator_grid_table_row}>
                        <div className={styles.basis_calculator_grid_table_cell_header}>
                            {period === BasisPeriod.Basis && translations.calculators.basis.basisPeriod[BasisPeriod.Basis]}
                            {period === BasisPeriod.ThreeMonthBasis && `3 ${translations.words.monthShort}`}
                            {period === BasisPeriod.SixMonthBasis && `6 ${translations.words.monthShort}`}
                            {period === BasisPeriod.NineMonthBasis && `9 ${translations.words.monthShort}`}
                            {period === BasisPeriod.TwelveMonthBasis && `12 ${translations.words.monthShort}`}
                        </div>
                        <div className={styles.basis_calculator_grid_table_cell_average}>
                            {period === BasisPeriod.Basis &&
                                formattingService.formatNumber(props.calculationResult?.basisAverage, { maximumDecimalPlaces: 3, zeroAsEmpty: true })}
                            {period === BasisPeriod.ThreeMonthBasis &&
                                formattingService.formatNumber(props.calculationResult?.threeMonthsBasisAverage, {
                                    maximumDecimalPlaces: 3,
                                    zeroAsEmpty: true,
                                })}
                            {period === BasisPeriod.SixMonthBasis &&
                                formattingService.formatNumber(props.calculationResult?.sixMonthsBasisAverage, { maximumDecimalPlaces: 3, zeroAsEmpty: true })}
                            {period === BasisPeriod.NineMonthBasis &&
                                formattingService.formatNumber(props.calculationResult?.nineMonthsBasisAverage, { maximumDecimalPlaces: 3, zeroAsEmpty: true })}
                            {period === BasisPeriod.TwelveMonthBasis &&
                                formattingService.formatNumber(props.calculationResult?.twelveMonthsBasisAverage, {
                                    maximumDecimalPlaces: 3,
                                    zeroAsEmpty: true,
                                })}
                        </div>
                        {deciles.map((_, index) => (
                            <div className={styles.basis_calculator_grid_table_cell}>
                                {period === BasisPeriod.Basis &&
                                    formattingService.formatNumber(props.calculationResult?.basisDeciles[index], {
                                        maximumDecimalPlaces: 3,
                                        zeroAsEmpty: true,
                                    })}
                                {period === BasisPeriod.ThreeMonthBasis &&
                                    formattingService.formatNumber(props.calculationResult?.threeMonthsBasisDeciles[index], {
                                        maximumDecimalPlaces: 3,
                                        zeroAsEmpty: true,
                                    })}
                                {period === BasisPeriod.SixMonthBasis &&
                                    formattingService.formatNumber(props.calculationResult?.sixMonthsBasisDeciles[index], {
                                        maximumDecimalPlaces: 3,
                                        zeroAsEmpty: true,
                                    })}
                                {period === BasisPeriod.NineMonthBasis &&
                                    formattingService.formatNumber(props.calculationResult?.nineMonthsBasisDeciles[index], {
                                        maximumDecimalPlaces: 3,
                                        zeroAsEmpty: true,
                                    })}
                                {period === BasisPeriod.TwelveMonthBasis &&
                                    formattingService.formatNumber(props.calculationResult?.twelveMonthsBasisDeciles[index], {
                                        maximumDecimalPlaces: 3,
                                        zeroAsEmpty: true,
                                    })}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default BasisCalculatorGrid;
